// Library Methods
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";
import SelectLensButton from "../card-ui/SelectLensButton/SelectLensButton";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Hooks
import { useSimulationEnvironmentTypes } from "src/services/clinics/hooks/clinic-lenses/simulation-environments/useSimulationEnvironmentTypes";
import { useDryEyeProfiles } from "src/services/clinics/hooks/clinic-lenses/dry-eyes/useDryEyeProfiles";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import {
  DEFAULT_DRY_EYE_SIMULATION_CARD,
  DEFAULT_SIM_ENVIRONMENT,
  ENABLED_SIM_ENVIRONMENTS,
  examCardEnums,
} from "src/constants/enums";
import { getDryEyesTooltipText } from "src/utils/exams/lensSimulationHelper";

// Utils

type DryEyeCardType = {
  selectedSimCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const DryEyeCard = ({
  selectedSimCard,
  updateExamCardProperty,
  onRemoveCard,
}: DryEyeCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const defaultSimEnv = useMemo(
    () => ({
      displayName: t(DEFAULT_SIM_ENVIRONMENT),
      value: DEFAULT_SIM_ENVIRONMENT,
    }),
    [t]
  );
  const [envType, setEnvType] = useState({ displayName: "", value: "" });

  const {
    data: dryEyeProfiles,
    isLoading: isLoadingProfiles,
    isError: isErrorProfiles,
  } = useDryEyeProfiles(clinicInfo?.id);

  const {
    data: envData,
    isLoading: isLoadingEnvs,
    isError: isErrorEnvs,
  } = useSimulationEnvironmentTypes();

  const formattedEnvOptions = useMemo(
    () =>
      (envData ?? [])
        ?.filter((data) => ENABLED_SIM_ENVIRONMENTS.includes(data))
        ?.map((data) => ({
          displayName: t(data),
          value: data,
        })),
    [t, envData]
  );

  const hasSimEnvs = useMemo(
    () =>
      formattedEnvOptions &&
      formattedEnvOptions?.length > 0 &&
      !isLoadingEnvs &&
      !isErrorEnvs,
    [formattedEnvOptions, isLoadingEnvs, isErrorEnvs]
  );

  const hasDryEyeProfiles = useMemo(
    () =>
      dryEyeProfiles &&
      dryEyeProfiles?.length > 0 &&
      !isLoadingProfiles &&
      !isErrorProfiles,
    [dryEyeProfiles, isLoadingProfiles, isErrorProfiles]
  );

  const examCardInfo = selectedSimCard ?? DEFAULT_DRY_EYE_SIMULATION_CARD;
  const { binocular, profileIds, simulationEnvironmentType } = examCardInfo;

  // on adding a new lens simulation, auto-select the first lens
  useEffect(() => {
    if (
      dryEyeProfiles &&
      dryEyeProfiles?.length > 0 &&
      profileIds &&
      !profileIds?.length
    ) {
      updateExamCardProperty("profileIds", [dryEyeProfiles?.[0]?.id]);
    }
  }, [profileIds, dryEyeProfiles, updateExamCardProperty]);

  // initial setup of simulation environment
  useEffect(() => {
    if (
      formattedEnvOptions &&
      formattedEnvOptions?.length > 0 &&
      !envType.value
    ) {
      const valueToUse = simulationEnvironmentType
        ? {
            displayName: t(simulationEnvironmentType),
            value: simulationEnvironmentType,
          }
        : defaultSimEnv;

      setEnvType({ ...valueToUse });
      updateExamCardProperty("simulationEnvironmentType", valueToUse?.value);
    }
  }, [
    t,
    envType.value,
    defaultSimEnv,
    simulationEnvironmentType,
    formattedEnvOptions,
    updateExamCardProperty,
  ]);

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  return (
    <ExamCardContainer>
      <ExamCardHeader
        label={t(examCardEnums.DRY_EYE_SIMULATION.buttonLabel)}
        onRemoveCard={onRemoveCard}
      />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        flexGrow={1}
        gap="8px"
      >
        {hasSimEnvs && (
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Starting Simulation Environment")}
            </Typography>
            <SelectDropdown
              id="sim-env-type-dropdown"
              dropdownOptions={formattedEnvOptions}
              value={envType.displayName}
              handleChange={(event) => {
                const value = formattedEnvOptions?.find(
                  (option) => option.displayName === event.target.value
                );

                if (value) {
                  setEnvType({
                    ...value,
                  });
                  updateExamCardProperty(
                    "simulationEnvironmentType",
                    value.value
                  );
                }
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <TitleWithTooltip
            title={t("Dry Eye Types")}
            tooltip={
              <div>
                {t("Controls which dry eye lenses appear in the simulation.")}
              </div>
            }
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid
            sx={{
              display: "grid",
              gridTemplateColumns:
                !hasDryEyeProfiles && !isLoadingProfiles
                  ? "repeat(1, 1fr)"
                  : "repeat(2, 1fr)",
              gap: "8px",
            }}
          >
            {isLoadingProfiles && (
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                {t("Loading...")}
              </Typography>
            )}
            {hasDryEyeProfiles &&
              dryEyeProfiles?.map((lensType) => (
                <SelectLensButton
                  key={`lens-button-${lensType.id}`}
                  lensType={lensType}
                  selectedLensArray={profileIds}
                  selectedIdsProperty="profileIds"
                  updateExamCardProperty={updateExamCardProperty}
                  examAlgorithmPath={
                    examCardEnums.DRY_EYE_SIMULATION.algorithmPath
                  }
                  tooltip={getDryEyesTooltipText(lensType, t)}
                />
              ))}
            {!hasDryEyeProfiles && !isLoadingProfiles && (
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                {t("No lenses found. You can add and remove lenses from the")}{" "}
                <Link
                  to="/settings"
                  style={{ color: "#FFF", textDecoration: "underline" }}
                >
                  {t("clinic settings page")}
                </Link>
                .
              </Typography>
            )}
          </Grid>
        </Box>

        {hasDryEyeProfiles && (
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            marginTop="auto"
          >
            <TitleWithTooltip
              title={t("Eye")}
              tooltip={t(
                "Choose the binocular option to test both eyes at once or the monocular option to test one eye at a time."
              )}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />
            <Grid container item display="flex" gap="8px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={Boolean(binocular)}
                  updateValue={() => updateExamCardProperty("binocular", true)}
                  label={t("Binocular")}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </ExamCardContainer>
  );
};

export default DryEyeCard;
